import type { NavigateFunction } from 'react-router-dom';
import { ROUTE_PATHS } from '../routes/routePaths';
import type { FilePermission, GetSharedData, ProductDocumentPermission } from '../types/common';
import { apiURL } from './constants';
import type { PdfViewerAnalyticsContext } from './pdfViewerAnalytics';

interface BuildDownloadRedirectUrlOptions {
  defaultPageNumber?: string;
}

interface OpenInPdfViewerOptions extends BuildDownloadRedirectUrlOptions {
  /** File extension from document metadata (e.g. `pdf`, `docx`). */
  extension?: string;
  /** Open the in-app PDF viewer in a new tab instead of same-tab navigate. */
  openInNewTab?: boolean;
  /**
   * Window opened synchronously during the user click (e.g. about:blank).
   * Avoids popup blockers that block window.open() after an await.
   * Do not open with `noopener` — that returns null and loses the reference.
   */
  targetWindow?: Window | null;
  /** Download-page href used to re-validate the document on viewer refresh. */
  sourceHref?: string;
  /** Entity/document context for `PDF_Viewer_Feature` PostHog events. */
  analyticsContext?: PdfViewerAnalyticsContext;
}

export function isPdfExtension(extension: string | null | undefined): boolean {
  return extension?.trim().toLowerCase() === 'pdf';
}

export function isProductPdfViewerEnabled(
  permission: ProductDocumentPermission | null | undefined
): boolean {
  return permission?.isEnabled === true;
}

export function toFilePermissionForViewer(
  permission: ProductDocumentPermission | null | undefined
): FilePermission | null {
  if (!permission) return null;
  return {
    renderingStyleId: 0,
    isEnabled: permission.isEnabled,
    features: permission.features ?? [],
  };
}

/** Extension from a filename (e.g. `Report.doc` → `doc`). Empty if none. */
export function getExtensionFromFileName(
  fileName: string | null | undefined,
): string {
  if (!fileName?.trim()) return '';
  const trimmed = fileName.trim();
  const lastDotIndex = trimmed.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === trimmed.length - 1) return '';
  return trimmed.slice(lastDotIndex + 1).replace(/^\./, '').toLowerCase();
}

/**
 * Prefer API `params.fileName` over a caller-supplied extension so non-PDFs
 * never open the in-app PDF viewer (even if a page hardcodes `extension: 'pdf'`).
 * Falls back to the storage `url` basename when `fileName` is missing — otherwise
 * `NEW_PAGE_PDF_DOWNLOAD` is incorrectly coerced to `DOWNLOAD` (forced download).
 */
export function resolveDocumentExtension(
  downloadData: GetSharedData,
  extensionHint?: string | null,
): string {
  const fromFileName = getExtensionFromFileName(downloadData?.params?.fileName);
  if (fromFileName) return fromFileName;

  const hint = extensionHint?.trim().toLowerCase() || '';
  if (hint) return hint;

  // url may be a token path ending in `.pdf` (e.g. `..._739.pdf`) with no params.fileName
  const urlBasename = downloadData?.url?.trim().split(/[/\\]/).pop() ?? '';
  return getExtensionFromFileName(urlBasename);
}

const PDF_VIEWER_SESSION_STATE_PREFIX = 'pdfViewerState:';

/** Storage key used to pass filePermission/fileName into a newly opened PDF viewer tab. */
export function buildPdfViewerSessionStateKey(pdfUrlParameter: string): string {
  return `${PDF_VIEWER_SESSION_STATE_PREFIX}${pdfUrlParameter}`;
}

export type PdfViewerSessionState = {
  filePermission: GetSharedData['filePermission'];
  fileName?: string;
  analyticsContext?: PdfViewerAnalyticsContext;
};

/** In-memory only — cleared on page reload (history state is not). */
const freshPdfViewerLoads = new Set<string>();

export function markFreshPdfViewerLoad(pdfUrlParameter: string): void {
  freshPdfViewerLoads.add(pdfUrlParameter);
}

export function isFreshPdfViewerLoad(pdfUrlParameter: string | null): boolean {
  return !!pdfUrlParameter && freshPdfViewerLoads.has(pdfUrlParameter);
}

export type PdfViewerBufferHandoff = {
  buffer: ArrayBuffer;
  filePermission?: FilePermission | null;
  fileName?: string;
  analyticsContext?: PdfViewerAnalyticsContext;
};

const pdfViewerBufferMemory = new Map<string, PdfViewerBufferHandoff>();

export function storePdfViewerBuffer(key: string, data: PdfViewerBufferHandoff): void {
  pdfViewerBufferMemory.set(key, data);
}

export function consumePdfViewerBuffer(key: string | null): PdfViewerBufferHandoff | null {
  if (!key) return null;
  return pdfViewerBufferMemory.get(key) ?? null;
}

export function storePdfViewerSessionState(
  pdfUrlParameter: string,
  state: PdfViewerSessionState
): void {
  try {
    // localStorage: shared across tabs (sessionStorage is not when using noopener)
    localStorage.setItem(
      buildPdfViewerSessionStateKey(pdfUrlParameter),
      JSON.stringify({ ...state, expiresAt: Date.now() + 60_000 })
    );
  } catch {
    // storage may be unavailable; viewer still opens with default permissions
  }
}

export function consumePdfViewerSessionState(
  pdfUrlParameter: string | null
): PdfViewerSessionState | null {
  if (!pdfUrlParameter) return null;
  try {
    const key = buildPdfViewerSessionStateKey(pdfUrlParameter);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    localStorage.removeItem(key);
    const parsed = JSON.parse(raw) as PdfViewerSessionState & {
      expiresAt?: number;
    };
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
      return null;
    }
    markFreshPdfViewerLoad(pdfUrlParameter);
    return {
      filePermission: parsed.filePermission,
      fileName: parsed.fileName,
      analyticsContext: parsed.analyticsContext,
    };
  } catch {
    return null;
  }
}

export const buildDownloadRedirectUrl = (
  downloadData: GetSharedData,
  options?: BuildDownloadRedirectUrlOptions
): string => {
  const redirectType = downloadData?.redirectEnum;
  const returnedUrl = downloadData?.url?.trim();
  const pageNumber = downloadData?.pageNumber ?? options?.defaultPageNumber?.trim() ?? '1';

  if (!redirectType || !returnedUrl) {
    throw new Error('Missing redirect information for PDF download.');
  }

  switch (redirectType) {
    case 'NEW_PAGE_PDF_DOWNLOAD': {
      const returnedFileName = downloadData?.params?.fileName?.trim();
      const fileNameQuery = returnedFileName ? `&fileName=${returnedFileName}` : '';
      return `${apiURL}/storage/download/${returnedUrl}?download=false${fileNameQuery}#page=${pageNumber}`;
    }
    case 'DOWNLOAD': {
      const returnedFileName = downloadData?.params?.fileName?.trim();
      const fileNameQuery = returnedFileName ? `&fileName=${returnedFileName}` : '';
      return `${apiURL}/storage/download/${returnedUrl}?download=true${fileNameQuery}`;
    }
    case 'PDF_PAGE_DOWNLOAD': {
      return `${ROUTE_PATHS.CK_EDITOR_DOCUMENT_PDF_PAGE_DOWNLOAD}?${returnedUrl}`;
    }
    case 'VIEW_CK_EDITOR_DOC': {
      return `${ROUTE_PATHS.CK_EDITOR_VIEW_DOCUMENT}?${returnedUrl}`;
    }
    case 'REDIRECT_TO_LINK': {
      return returnedUrl;
    }
    default: {
      throw new Error(`Unsupported redirect type: ${redirectType}`);
    }
  }
};

export const redirectToDownloadTarget = (
  downloadData: GetSharedData,
  options?: BuildDownloadRedirectUrlOptions
): void => {
  const redirectUrl = buildDownloadRedirectUrl(downloadData, options);
  window.location.replace(redirectUrl);
  if (downloadData?.redirectEnum === 'DOWNLOAD' || downloadData?.redirectEnum === 'NEW_PAGE_PDF_DOWNLOAD') {
  setTimeout(() => { 
    window.close(); 
    if (!window.closed) {
      window.location.replace(ROUTE_PATHS.DOWNLOAD_SUCCESS);
    }
  }, 3000); // keep it open at least 3 seconds
}
};

/**
 * Common logic used by download pages:
 * - PDF + `filePermission.isEnabled === true` → in-app PDF viewer.
 * - PDF + `filePermission.isEnabled === false` → open / download PDF.
 * - Non-PDF (viewer on or off) → direct download (never PDF viewer).
 */
export function openInPdfViewerOrRedirect(
  downloadData: GetSharedData,
  navigate: NavigateFunction,
  options?: OpenInPdfViewerOptions,
): void {
  const extension = resolveDocumentExtension(downloadData, options?.extension);
  const isPdf = isPdfExtension(extension);
  const useInAppPdfViewer = downloadData.filePermission?.isEnabled === true;
  if (
    (downloadData.redirectEnum === 'NEW_PAGE_PDF_DOWNLOAD' || downloadData.redirectEnum === 'DOWNLOAD') &&
    isPdf &&
    useInAppPdfViewer
  ) {
    const returnedUrl = downloadData?.url?.trim();
    if (!returnedUrl) {
      throw new Error('Missing PDF URL in download response.');
    }

    const returnedFileName = downloadData?.params?.fileName?.trim();
    const fileNameQuery = returnedFileName ? `&fileName=${returnedFileName}` : '';
    const pageNumber = downloadData?.pageNumber ?? options?.defaultPageNumber?.trim() ?? '1';
    const pdfPath = `storage/download/${returnedUrl}?download=false${fileNameQuery}`;
    const sourceHref =
      options?.sourceHref ??
      (options?.openInNewTab
        ? undefined
        : `${globalThis.location.pathname}${globalThis.location.search}`);
    const sourceQuery = sourceHref ? `&src=${encodeURIComponent(sourceHref)}` : '';
    const viewerPath = `${ROUTE_PATHS.PDF_VIEWER}?pdfUrl=${encodeURIComponent(pdfPath)}${sourceQuery}#page=${pageNumber}`;

    if (options?.openInNewTab) {
      storePdfViewerSessionState(pdfPath, {
        filePermission: downloadData.filePermission,
        fileName: returnedFileName,
        analyticsContext: options.analyticsContext,
      });
      const absoluteViewerUrl = new URL(
        viewerPath,
        globalThis.location.origin
      ).toString();
      const target = options.targetWindow;
      if (target && !target.closed) {
        target.location.href = absoluteViewerUrl;
      } else {
        globalThis.window.open(absoluteViewerUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    markFreshPdfViewerLoad(pdfPath);
    void navigate(viewerPath, {
      state: {
        filePermission: downloadData.filePermission,
        fileName: returnedFileName,
        analyticsContext: options?.analyticsContext,
      },
    });
    return;
  }

  const redirectData =
    downloadData.redirectEnum === 'NEW_PAGE_PDF_DOWNLOAD' && !isPdf
      ? { ...downloadData, redirectEnum: 'DOWNLOAD' }
      : downloadData;

  redirectToDownloadTarget(redirectData, { defaultPageNumber: options?.defaultPageNumber });
}